import { DefaultLoader, LoaderContainer } from 'components/Loader';
import { PageHeadContainer, PageTitle } from 'components/PageHead';
import MentorFormEdit from 'components/UserForms/MentorForm/MentorFormEdit';
import {
  Errors,
  initialErrors,
  State,
} from 'components/UserForms/MentorForm/types';
import { FC, useEffect, useState } from 'react';
import useSWR, { mutate } from 'swr';
import MentorFormType from 'types/MentorForm';
import privateAPI from 'utils/privateAxiosInstance';

const Head: FC = () => {
  return (
    <PageHeadContainer>
      <PageTitle>
        <h1>Questionnaire de mentorat</h1>
      </PageTitle>
    </PageHeadContainer>
  );
};

const initialState: State = {
  jobSearch: null,
  careerPlan: null,
  studies: null,
  entrepreneurship: null,
  expertises: [],
  formations: [],
  diploma: [],
  consideration: null,
  hobby: null,
  wantedMentorships: 0,
  heardAboutMentorship: null,
  heardAboutMentorshipText: null,
  expectations: null,
  sharingPhone: null,
  sharingEmail: null,
};

const EditFormMentor: FC = () => {
  const [state, setState] = useState<State>(initialState);
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);

  const { data: mentorForm, error } = useSWR<MentorFormType>('/mentor-form/');

  useEffect(() => {
    if (mentorForm) {
      setState({
        jobSearch: mentorForm.jobSearch!,
        careerPlan: mentorForm.careerPlan!,
        studies: mentorForm.studies!,
        entrepreneurship: mentorForm.entrepreneurship!,
        expertises: mentorForm.expertiseFields!.map(
          (expertiseField) => expertiseField.expertise,
        ),
        formations: mentorForm.formations!.map(
          (mentorFormation) => mentorFormation.formation,
        ),
        diploma: mentorForm.diploma!.map(
          (mentorDiploma) => mentorDiploma.diploma,
        ),
        consideration: mentorForm.consideration!,
        hobby: mentorForm.hobby!,
        wantedMentorships: mentorForm.wantedMentorships!,
        heardAboutMentorship: mentorForm.heardAboutMentorship || null,
        heardAboutMentorshipText: mentorForm.heardAboutMentorshipText || null,
        expectations: mentorForm.expectations || null,
        sharingPhone: mentorForm.sharingPhone!,
        sharingEmail: mentorForm.sharingEmail!,
      });
    }
  }, [mentorForm]);

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess(false);
    setErrors(initialErrors);

    if (!(state.sharingEmail || state.sharingPhone)) {
      setLoading(false);
      return setErrors({
        ...initialErrors,
        permissions: true,
      });
    }

    privateAPI
      .post('/mentor-form/', {
        ...state,
        diploma: state.diploma,
      })
      .then(() => {
        mutate('/mentor-form/');
        setLoading(false);
        setSuccess(true);
        window.scrollTo({ top: 0 });
      })
      .catch((err) => {
        window.scrollTo({ top: 0 });
        setLoading(false);
        if (err?.response?.status === 400) {
          return setErrors({
            ...initialErrors,
            badRequest: true,
          });
        }

        setErrors({
          ...initialErrors,
          general: true,
        });
      });
  };

  return mentorForm || error ? (
    <>
      <Head />
      <MentorFormEdit
        title="Questionnaire de mentorat"
        subtitle={[
          'Grâce à vos réponses, nous identifierons le jeune avec lequel vous serez mis en relation.',
          'Ce jeune aura répondu à un questionnaire similaire, ce qui nous permettra de plus facilement identifier les binômes à constituer.',
          'IMPORTANT : les binômes seront formés selon la complémentarité de vos profils plutôt que sur la proximité géographique.',
        ]}
        marginTop="0.375rem"
        {...{
          state,
          setState,
          errors,
          setErrors,
          handleSubmit,
          loading,
          success,
        }}
        invalid={mentorForm?.invalid}
      />
    </>
  ) : (
    <LoaderContainer>
      <DefaultLoader size={1.5} />
    </LoaderContainer>
  );
};

export default EditFormMentor;
